"use client"

import axios from "axios"
import useRequestIdGenerater from "customhooks"
import { useEffect, useState } from "react"
import useTimeStamp from "reuseableTable/uniquetimestamp/GenerateUniqueTimeStamp"
import MainCard from "ui-component/cards/MainCard"
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Checkbox,
    FormControlLabel,
    InputAdornment,
    Grid,
    FormGroup,
    Button,
    CircularProgress,
    TablePagination,
} from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import AnimatedLoader from "reuseableTable/AnimatedLoader"
import SearchResponseData from "./SearchResponseData"
import NoAvailableData from "reuseableTable/NoAvailableData"

const FilterCard = ({ title, data, onSelectionChange }) => {
    const [searchTerm, setSearchTerm] = useState("")
    const [selectedItem, setSelectedItem] = useState("")

    const filteredData = data.filter(
        (item) => item.toLowerCase().includes(searchTerm.toLowerCase()) && item.trim() !== "",
    )

    const handleCheckboxChange = (item) => {
        const newItem = item === selectedItem ? "" : item
        setSelectedItem(newItem)
        onSelectionChange(title, newItem ? [newItem] : [])
    }

    return (
        <Card sx={{ height: "100%", boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)", borderRadius: "10px" }}>
            <CardContent>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                    {title}
                </Typography>
                <TextField
                    placeholder="Search"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ mb: 2, bgcolor: "#f5f5f5", borderRadius: "4px" }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                <Box sx={{ maxHeight: "200px", overflowY: "auto" }}>
                    <FormGroup>
                        {filteredData.map((item, index) => (
                            <FormControlLabel
                                key={index}
                                control={
                                    <Checkbox
                                        checked={selectedItem === item}
                                        onChange={() => handleCheckboxChange(item)}
                                    />
                                }
                                label={item}
                            />
                        ))}
                    </FormGroup>
                </Box>
            </CardContent>
        </Card>
    )
}


const AdvanceSearch = () => {
    const [filters, setFilters] = useState({
        Institute: [],
        "Program Name": [],
        Organization: [],
        "Functional Area": [],
        Industries: [],
    })
    const [selectedFilters, setSelectedFilters] = useState({})
    const [loadingResponseData, setLoadingResponseData] = useState(false)
    const [responseData, setResponseData] = useState([]);

    const [page, setPage] = useState(0);
    const [pageCache, setPageCache] = useState({});
    const [rowsPerPage, setRowsPerPage] = useState(11);
    const [totalCount, setTotalCount] = useState(0);

    const RequestId = useRequestIdGenerater()
    const { rendomRequestId, generateTimeStamp } = useTimeStamp()
    const getRole = localStorage.getItem("role")

    const ReuseAbleApiCall = async (endpoint, processResponse) => {
        let url = `/v1/alumni/${endpoint}`
        const instituteId = getRole === "ROLE_SUPER_ADMIN" ? localStorage.getItem("instituteId") : ""

        if (instituteId) {
            url += `?instituteId=${instituteId}`
        }

        try {
            const response = await axios.get(url, {
                headers: {
                    Authorization: `Bearer ${window.localStorage.getItem("serviceToken")}`,
                    "Content-Type": "application/json",
                    requestId: RequestId(
                        Math.floor(1000 + Math.random() * 9999),
                        Math.floor(1000 + Math.random() * 9999 + rendomRequestId),
                    ),
                },
            })

            processResponse(response)
            generateTimeStamp()
        } catch (err) {
            generateTimeStamp()
            console.log(err)
        }
    }

    const fetchFilterData = (endpoint, category) => {
        ReuseAbleApiCall(endpoint, (response) => {
            setFilters((prev) => ({
                ...prev,
                [category]: response?.data?.data || [],
            }))
        })
    }

    useEffect(() => {
        fetchFilterData("organization-list", "Organization")
        fetchFilterData("institute-list", "Institute")
        fetchFilterData("program-list", "Program Name")
        fetchFilterData("functional-area-list", "Functional Area")
        fetchFilterData("industries-list", "Industries")
    }, [])

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
        setPageCache({});
    };


    const handleSelectionChange = (category, selectedItems) => {
        setPage(0)
        setSelectedFilters((prev) => ({
            ...prev,
            [category]: selectedItems,
        }))
    }
    const handleSearch = async () => {
        setLoadingResponseData(true)
        const payloadKeyMap = {
            "Organization": "orgName",
            "Functional Area": "workFuncArea",
            "Institute": "institute",
            "Program Name": "courseName",
            "Industries": "industry"
        }

        const payload = {}

        Object.entries(selectedFilters).forEach(([category, values]) => {
            if (values.length > 0) {
                const key = payloadKeyMap[category]
                if (key) {
                    payload[key] = values[0]
                }
            }
        })
        try {
            const response = await axios.post(`/v1/alumni/alumni-search?pageNumber=${page}&pageSize=${rowsPerPage + 1}`, payload, {
                headers: {
                    Authorization: `Bearer ${window.localStorage.getItem("serviceToken")}`,
                    "Content-Type": "application/json",
                    requestId: RequestId(
                        Math.floor(1000 + Math.random() * 9999),
                        Math.floor(1000 + Math.random() * 9999 + rendomRequestId),
                    ),
                },
            })

            generateTimeStamp()
            const alumniId = localStorage.getItem("alumniId")
            setResponseData(response?.data?.data?.content.filter((item) => item.id !== alumniId))
            if (
                response?.data?.data?.content.some((item) => item.id === alumniId)
            ) {
                setTotalCount(response?.data?.data?.totalElements - 1);
            } else {
                setTotalCount(response?.data?.data?.totalElements);
            }
            setLoadingResponseData(false)
        } catch (err) {
            generateTimeStamp()
            console.error("Search error:", err)
            setLoadingResponseData(false)
        }

    }

    useEffect(() => {
        if (pageCache[page]) {
            setResponseData(pageCache[page]);
        } else {
            handleSearch();
        }
    }, [page, rowsPerPage]);

    return (
        <MainCard>
            <Grid container spacing={2}>
                {Object.entries(filters).map(([category, data]) => (
                    <Grid item xs={12} sm={6} md={4} lg={2.4} key={category}>
                        <FilterCard title={category} data={data} onSelectionChange={handleSelectionChange} />
                    </Grid>
                ))}

                <Grid item md={12} xs={12} lg={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button startIcon={
                        loadingResponseData ? (
                            <CircularProgress size="1rem" sx={{ color: "red" }} />
                        ) : undefined
                    } disabled={loadingResponseData} variant="contained" onClick={handleSearch}>{loadingResponseData ? "Please Wait" : "Search"}</Button>
                </Grid>
            </Grid>

            {loadingResponseData ? (
                <AnimatedLoader />
            ) : (
                <>
                    {responseData?.length > 0 ? (
                        <>
                            <SearchResponseData setResponseData={setResponseData} responseData={responseData} />


                            <Grid item xs={12}>
                                <Grid container justifyContent="space-between" spacing={1}>
                                    <TablePagination
                                        component="div"
                                        count={totalCount}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        rowsPerPage={rowsPerPage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                    />
                                </Grid>
                            </Grid>

                        </>
                    ) : (
                        <Grid mt={1}>
                            <NoAvailableData text="No Data Found." />
                        </Grid>
                    )}
                </>
            )}


        </MainCard>
    )
}

export default AdvanceSearch;

